import React, { useMemo } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { Header, PermissionWrapper } from '@app/components'
import { LntButton } from '@app/components/base'
import { TabsLayout } from '@app/layouts'
import { FileUploadModal, PhotosUploadModal } from '@app/modals'

import { completeTask, matchTask } from '@app/api/routes/monitoringTasks'
import {
  completeCorrectionTask,
  getTaskItem,
  postUpdatePrices,
  postUploadPrices
} from '@app/api/routes/monitoringTasks/monitoringTasks'
import { exportMatchingTemplateFile, exportTaskValidationsTechCheckFile } from '@app/utils/export/exportFile'
import { useNotification, useToggle, useExtendedNavigate, useMultiStatusMutation } from '@app/hooks'

import { FILE_TYPES, MONITORING_TASK_STATUS } from '@app/api/constants'
import { MONITORINGS_TASKS_KEY } from '@app/constants/queryKeys'
import { PERMISSION } from '@app/constants/permission'
import { CONTRACTOR_SECTION_PAGE } from '@app/constants/page'
import { BUTTON_TYPE, SIZE, dateFormat } from '@app/constants'

import { uploadFileFactory } from '@app/utils'
import { getFormattedDate } from '@app/utils/date'

import { MonitoringTaskDto } from '@app/api/routes/monitoringTasks/dtos'

import { Show, Switch } from '@app/components/util'

import { CompetitorsPricesTab, PhotoControlTab } from './Tabs'
import styles from './styles.module.scss'

import type { ReactNode } from 'react'
import type { TabsLayoutItem } from '@app/layouts/TabsLayout/types'
import type { ContractorSectionPageInnerProps, MonitoringStatusType } from './types'

const { Case } = Switch

const tabsFactory = (taskId: number, itemStatus?: MonitoringStatusType | null): TabsLayoutItem[] => [
  {
    label: 'Цены конкурентов',
    key: 'competitorsPrices',
    content: (
      <CompetitorsPricesTab
        taskId={ taskId }
        status={ itemStatus }
      />
    )
  },
  {
    label: 'Фотоконтроль',
    key: 'photoControl',
    content: <PhotoControlTab taskId={ taskId } />
  }
]

const getPageTitle = (monitoringTaskItemData?: MonitoringTaskDto): string | undefined => {
  if (monitoringTaskItemData) {
    const monitoringTypeName = monitoringTaskItemData.monitoringType?.name ?? ''
    const storeName = monitoringTaskItemData.store?.name ?? ''
    const clusterName = monitoringTaskItemData.cluster?.name ?? ''
    const clustersGroupName = monitoringTaskItemData.clustersGroup?.name ?? ''

    return `${monitoringTypeName}, ${storeName || clusterName || clustersGroupName}`
  }
}

const getTemplateExcelName = (monitoringTaskItemData?: MonitoringTaskDto): string => {
  const clusterName = monitoringTaskItemData?.cluster?.name ?? ''
  const monitoringTypeName = monitoringTaskItemData?.monitoringType?.name ?? ''
  const monitoringTaskData = getFormattedDate(monitoringTaskItemData?.monitoringStartAt, dateFormat['DD-MM-YYYY']) ?? ''

  return `ЛC_${monitoringTaskData}_${clusterName}_${monitoringTypeName}`
}

export const ContractorSectionPageInner: React.FC<ContractorSectionPageInnerProps> = ({ id }) => {
  const notification = useNotification()
  const navigate = useExtendedNavigate()
  const queryClient = useQueryClient()

  const uploadPricesModalToggle = useToggle()
  const updatePricesModalToggle = useToggle()
  const uploadPhotosModalToggle = useToggle()

  const { data: monitoringTaskItemData, isLoading: isLoadingmMonitoringTaskItem } = useQuery({
    queryKey: [MONITORINGS_TASKS_KEY.tasksItem, { id }],
    queryFn: async () => await getTaskItem(id)
  })

  const taskItemStatus = useMemo(() => monitoringTaskItemData?.status, [monitoringTaskItemData])

  const tabs: TabsLayoutItem[] = tabsFactory(id, taskItemStatus)
  const pageTitle = useMemo(() => getPageTitle(monitoringTaskItemData), [monitoringTaskItemData])

  const matchTaskMutation = useMutation({
    mutationFn: matchTask,
    onSuccess: async () => {
      await Promise.allSettled([
        queryClient.invalidateQueries({ queryKey: [MONITORINGS_TASKS_KEY.tasksItem] }),
        queryClient.invalidateQueries({ queryKey: [MONITORINGS_TASKS_KEY.tasksItems] }),
        queryClient.invalidateQueries({ queryKey: [MONITORINGS_TASKS_KEY.tasksResults] }),
        queryClient.invalidateQueries({ queryKey: [MONITORINGS_TASKS_KEY.tasksItemPhotos] })
      ])

      notification.success({
        message: 'Cопоставление выполнено',
        description: 'Товары и фото сопоставлены, можно завершать мониторинг'
      })
    }
  })

  const handlePhotosUploadFinish = async (): Promise<void> => {
    await Promise.allSettled([
      queryClient.invalidateQueries({ queryKey: [MONITORINGS_TASKS_KEY.tasksResults] }),
      queryClient.invalidateQueries({ queryKey: [MONITORINGS_TASKS_KEY.tasksItemPhotos] })
    ])
  }

  const handleTaskValidationsTechCheckFile = async (): Promise<void> => {
    const fileName = 'Список_технических_ошибок'

    await exportTaskValidationsTechCheckFile(fileName, { id, file: FILE_TYPES.xlsx })

    await queryClient.invalidateQueries({ queryKey: [MONITORINGS_TASKS_KEY.tasksItems] })
  }

  const { mutation: postUploadPricesMutation, modalContextHolder: uploadPricesModalContextHolder } =
    useMultiStatusMutation({
      mutationFn: postUploadPrices,
      onSuccess: async () => {
        notification.success({
          message: 'Успешно:',
          description: 'Цены успешно загружены!'
        })
      },
      footer: (
        <LntButton
          type={ BUTTON_TYPE.secondary }
          size={ SIZE.middle }
          onClick={ handleTaskValidationsTechCheckFile }
        >
          Экспорт ошибок
        </LntButton>
      )
    })

  const { mutation: postUpdatePricesMutation, modalContextHolder: updatePricesModalContextHolder } =
    useMultiStatusMutation({
      mutationFn: postUpdatePrices,
      onSuccess: async () => {
        notification.success({
          message: 'Успешно:',
          description: 'Цены успешно обновлены!'
        })
      }
    })

  const handleDownloadPrices = uploadFileFactory({
    id,
    callback: async (id: number, file: File): Promise<void> => {
      const pricesFormData = new FormData()
      pricesFormData.append('file', file, file.name)

      const data = pricesFormData

      try {
        await postUploadPricesMutation.mutateAsync({ id, data })
      } finally {
        await queryClient.invalidateQueries({ queryKey: [MONITORINGS_TASKS_KEY.tasksResults] })
        uploadPricesModalToggle.setUntoggled()
      }
    }
  })

  const handleUpdatePrices = uploadFileFactory({
    id,
    callback: async (id: number, file: File): Promise<void> => {
      const pricesFormData = new FormData()

      pricesFormData.append('file', file, file.name)

      const data = pricesFormData

      try {
        await postUpdatePricesMutation.mutateAsync({ id, data })
      } finally {
        await queryClient.invalidateQueries({ queryKey: [MONITORINGS_TASKS_KEY.tasksResults] })
        updatePricesModalToggle.setUntoggled()
      }
    }
  })

  const handleMatchProductAndPhoto = async (): Promise<void> => {
    matchTaskMutation.mutate({ id })
  }

  const handleExportMatchingTemplate = async (): Promise<void> => {
    const fileName = getTemplateExcelName(monitoringTaskItemData)

    await exportMatchingTemplateFile(fileName, { id, file: FILE_TYPES.xlsx })
  }

  const handleNavigateToContractorSectionPage = (): void => {
    navigate(-1)
  }

  const handleCompleteTask = async (): Promise<void> => {
    await completeTask({ id })

    navigate(CONTRACTOR_SECTION_PAGE.contractorSection)
  }

  const handleCompleteCorrectionTask = async (): Promise<void> => {
    await completeCorrectionTask({ id })

    navigate(CONTRACTOR_SECTION_PAGE.contractorSection)
  }

  const fallbackSubheaderToolbar: ReactNode[] = [
    <PermissionWrapper
      key='compare-products-and-photos'
      requiredPermissions={ [PERMISSION.downloadMonitoringResults] }
    >
      <LntButton
        type={ BUTTON_TYPE.secondary }
        size={ SIZE.small }
        onClick={ handleMatchProductAndPhoto }
      >
        Сопоставить товары и фото
      </LntButton>
    </PermissionWrapper>,

    <PermissionWrapper
      key='download-comparability-sheet'
      requiredPermissions={ [PERMISSION.downloadMonitoringResults] }
    >
      <LntButton
        type={ BUTTON_TYPE.secondary }
        size={ SIZE.small }
        onClick={ handleExportMatchingTemplate }
      >
        Скачать лист сопоставимости
      </LntButton>
    </PermissionWrapper>,

    <PermissionWrapper
      key='upload-prices'
      requiredPermissions={ [PERMISSION.downloadMonitoringResults] }
    >
      <Switch
        fallback={
          <LntButton
            type={ BUTTON_TYPE.secondary }
            size={ SIZE.small }
            onClick={ uploadPricesModalToggle.setToggled }
          >
            Загрузить цены
          </LntButton>
        }
      >
        <Case condition={ monitoringTaskItemData?.status === MONITORING_TASK_STATUS.NEEDS_CORRECTION }>
          <LntButton
            type={ BUTTON_TYPE.secondary }
            size={ SIZE.small }
            onClick={ updatePricesModalToggle.setToggled }
          >
            Обновить цены
          </LntButton>
        </Case>
      </Switch>
    </PermissionWrapper>,

    <PermissionWrapper
      key='upload-photos'
      requiredPermissions={ [PERMISSION.downloadMonitoringResults] }
    >
      <LntButton
        type={ BUTTON_TYPE.secondary }
        size={ SIZE.small }
        onClick={ uploadPhotosModalToggle.setToggled }
      >
        Загрузить фото
      </LntButton>
    </PermissionWrapper>,

    <Show
      key='finish-monitoring'
      condition={ monitoringTaskItemData?.status === MONITORING_TASK_STATUS.IN_PROGRESS }
    >
      <LntButton
        type={ BUTTON_TYPE.text }
        size={ SIZE.small }
        onClick={ handleCompleteTask }
      >
        Завершить мониторинг
      </LntButton>
    </Show>,

    <Show
      key='finish-complete-monitoring'
      condition={ monitoringTaskItemData?.status === MONITORING_TASK_STATUS.NEEDS_CORRECTION }
    >
      <LntButton
        type={ BUTTON_TYPE.text }
        size={ SIZE.small }
        onClick={ handleCompleteCorrectionTask }
      >
        Завершить мониторинг
      </LntButton>
    </Show>
  ]

  return (
    <>
      <Header controlProps={ { title: pageTitle, onPushBack: handleNavigateToContractorSectionPage } } />

      <Show condition={ !isLoadingmMonitoringTaskItem }>
        <TabsLayout
          tabs={ tabs }
          className={ styles.tabs }
          fallbackSubheaderToolbar={ fallbackSubheaderToolbar }
        />
      </Show>

      <FileUploadModal
        title='Добавление цен конкурентов'
        open={ uploadPricesModalToggle.value }
        onCancel={ uploadPricesModalToggle.setUntoggled }
        onFileUpload={ handleDownloadPrices }
      />

      <FileUploadModal
        title='Обновление цен'
        open={ updatePricesModalToggle.value }
        onCancel={ updatePricesModalToggle.setUntoggled }
        onFileUpload={ handleUpdatePrices }
      />

      <PhotosUploadModal
        taskId={ id }
        open={ uploadPhotosModalToggle.value }
        onCancel={ uploadPhotosModalToggle.setUntoggled }
        title='Добавление фото'
        onPhotosUploadFinish={ handlePhotosUploadFinish }
      />

      { uploadPricesModalContextHolder }
      { updatePricesModalContextHolder }
    </>
  )
}
